// HyperpopDragSort — Tier-A "Hyperpop Arcade" renderer for the drag-sort
// component (the world's SECOND home-set pairing, after reveal-predict).
//
// Reuses the existing Hyperpop toolkit registration (atmosphere + chiptune audio
// cue palette in lib/scenario-themes/) — no new world infra. Reframes drag-sort as
// an arcade SORTING GAME: candy chips dropped into chunky bins; "LOCK IN MY SORT!"
// slams the verdict with confetti + a screen flash; correct chips pop green, wrong
// chips shake red.
//
// State machine + onActivityResponse mirror DragSortSlide's `Student` exactly:
// per-zone ordered itemIds (`zoneOrder`) + `submitted`; on submit it emits the
// EXACT
//   { activityType:'drag-sort', mode:'quiz',
//     responseData:{ placements:[{itemId,zoneId,position?}…] }, isCorrect, score }
// payload — `position` is the 0-based index within the zone (sequence mode only),
// and sequence scoring honours `correctPosition` just like the standard renderer.
//
// Result view (July audit §8.7 repairs):
//   - Authored "So what?" close via the shared <ExperienceClose>, same
//     {correct, total} tokens the standard skin passes, framed in hyperpop chrome.
//   - "Fix the misses" correction loop: returns only the wrongly placed items to
//     the pool for another round (arcade "beat your own score" grammar — honest
//     here because drag-sort HAS right answers). The corrected commit re-emits
//     the full composite payload, overwriting the record (standard semantics).
//   - "Start over" full reset (registry minimum-viable).
//   - Tap-to-select touch fallback: tap a chip, then tap a bin to place it —
//     alongside dnd-kit drag + keyboard (registry minimum-viable).

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragEndEvent,
} from '@dnd-kit/core';
import type { DragSortSlideData, Theme } from '../../slideTypes';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio, playHyperpopWin, playHyperpopLose } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';

const STYLE_ID = 'hyperpop-ds-scoped-styles';
const POOL_ZONE = '__pool__';

const BLUE = '#2d5cff';
const PINK = '#ff3d9a';
const YELLOW = '#ffe600';
const LIME = '#3ddc84';
const TANGERINE = '#ff6b1a';
const INK = '#0a0a0a';
const CONFETTI = [BLUE, PINK, YELLOW, LIME, TANGERINE];
const ZONE_INKS = [BLUE, PINK, TANGERINE, LIME];

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced `.hyperds`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.hyperds {
  position: relative;
  --ink: ${INK};
  --shadow: 6px 6px 0 var(--ink);
  --shadow-sm: 3px 3px 0 var(--ink);
  --shadow-lg: 12px 12px 0 var(--ink);
  --border: 4px solid var(--ink);
  --spring: cubic-bezier(.34,1.56,.64,1);
  --spring-big: cubic-bezier(.18,1.85,.5,1);
}
.hyperds * { box-sizing: border-box; }

.hyperds .hd-kicker {
  display: inline-block; font-weight: 800; font-size: 1.2rem; letter-spacing: .04em; text-transform: uppercase;
  background: ${PINK}; color: #fff; -webkit-text-stroke: 1px var(--ink);
  border: var(--border); border-radius: 14px; padding: 7px 20px; box-shadow: var(--shadow); transform: rotate(-3deg);
}
.hyperds .hd-h1 { font-weight: 800; font-size: clamp(1.9rem, 5.5vw, 3.6rem); line-height: .96; text-align: center; margin: 22px 0 6px; transform: rotate(-1deg); text-wrap: balance; }
.hyperds .hd-instruction { max-width: 600px; margin: 14px auto 0; font-size: 1rem; font-weight: 500; line-height: 1.4; background: ${YELLOW}; border: var(--border); border-radius: 16px; padding: 14px 20px; box-shadow: var(--shadow-sm); transform: rotate(.8deg); text-align: center; }

.hyperds .hd-zone {
  border: 4px dashed var(--ink); border-radius: 18px; padding: 14px 14px 16px; min-height: 96px;
  background: rgba(255,255,255,0.55); transition: background .15s, transform .12s, box-shadow .12s;
}
.hyperds .hd-zone.pool { border-style: solid; background: rgba(255,255,255,0.7); box-shadow: var(--shadow-sm); }
.hyperds .hd-zone.over { background: #fff; transform: translate(-2px,-2px); box-shadow: var(--shadow); }
.hyperds .hd-zone-label { display: inline-block; font-weight: 800; font-size: .82rem; text-transform: uppercase; letter-spacing: .08em; color: #fff; -webkit-text-stroke: .5px var(--ink); border: 3px solid var(--ink); border-radius: 999px; padding: 3px 12px; margin-bottom: 10px; box-shadow: 2px 2px 0 var(--ink); }
.hyperds .hd-zone-empty { font-size: .82rem; font-style: italic; color: #6b6456; opacity: .8; }
.hyperds .hd-chips { display: flex; flex-wrap: wrap; gap: 10px; }

.hyperds .hd-chip {
  font-weight: 700; font-size: .95rem; line-height: 1.2; color: var(--ink);
  background: #fff; border: 3px solid var(--ink); border-radius: 12px; padding: 9px 13px;
  box-shadow: var(--shadow-sm); cursor: grab; touch-action: none; user-select: none;
  transition: box-shadow .12s, background .2s, color .2s;
}
.hyperds .hd-chip:hover { box-shadow: var(--shadow); }
.hyperds .hd-chip.dragging { cursor: grabbing; box-shadow: var(--shadow-lg); z-index: 50; opacity: .95; }
.hyperds .hd-chip.correct { background: ${LIME} !important; animation: hdPop .45s var(--spring-big); }
@keyframes hdPop { 0% { transform: scale(1.18) rotate(-3deg); } 60% { transform: scale(.94) rotate(1deg); } 100% { transform: scale(1) rotate(0); } }
.hyperds .hd-chip.wrong { background: #ff2d2d !important; color: #fff !important; animation: hdShake .42s ease; }
@keyframes hdShake { 0%,100% { transform: translateX(0); } 20% { transform: translateX(-7px) rotate(-2deg); } 40% { transform: translateX(7px) rotate(2deg); } 60% { transform: translateX(-5px); } 80% { transform: translateX(5px); } }
.hyperds .hd-chip.selected { background: ${BLUE} !important; color: #fff !important; box-shadow: var(--shadow); transform: translate(-2px,-2px); }
.hyperds .hd-zone.targetable { cursor: pointer; }
.hyperds .hd-tap-hint { margin: 10px auto 0; text-align: center; font-weight: 700; font-size: .85rem; text-transform: uppercase; letter-spacing: .06em; color: ${BLUE}; }
.hyperds .hd-round-chip { display: inline-block; font-weight: 800; font-size: .9rem; text-transform: uppercase; letter-spacing: .05em; background: ${TANGERINE}; color: #fff; -webkit-text-stroke: .5px var(--ink); border: 3px solid var(--ink); border-radius: 999px; padding: 5px 16px; box-shadow: var(--shadow-sm); transform: rotate(-1.5deg); }

.hyperds .hd-reorder { display: inline-flex; flex-direction: column; gap: 3px; }
.hyperds .hd-reorder-btn {
  font-weight: 800; font-size: .7rem; line-height: 1; color: var(--ink);
  background: #fff; border: 2px solid var(--ink); border-radius: 7px; padding: 3px 6px;
  box-shadow: 2px 2px 0 var(--ink); cursor: pointer; transition: transform .1s, box-shadow .1s;
}
.hyperds .hd-reorder-btn:hover:not(:disabled) { transform: translate(-1px,-1px); box-shadow: 3px 3px 0 var(--ink); }
.hyperds .hd-reorder-btn:active:not(:disabled) { transform: translate(1px,1px); box-shadow: 1px 1px 0 var(--ink); }
.hyperds .hd-reorder-btn:disabled { opacity: .3; cursor: default; box-shadow: 1px 1px 0 var(--ink); }

.hyperds .hd-submit {
  width: 100%; margin-top: 26px; font-weight: 800; font-size: clamp(1.3rem,4vw,2rem); line-height: 1; text-transform: uppercase; letter-spacing: .02em;
  color: var(--ink); background: ${YELLOW}; -webkit-text-stroke: 1px var(--ink);
  border: var(--border); border-radius: 20px; padding: 18px 28px; box-shadow: var(--shadow-lg); cursor: pointer; transform: rotate(-1deg);
  transition: transform .15s var(--spring), box-shadow .15s var(--spring), background .2s, opacity .2s, filter .2s;
}
.hyperds .hd-submit:not(:disabled) { animation: hdPulse 1.3s ease-in-out infinite; }
@keyframes hdPulse { 0%,100% { transform: rotate(-1deg) scale(1); } 50% { transform: rotate(-1deg) scale(1.025); } }
.hyperds .hd-submit:hover:not(:disabled) { transform: rotate(0) translate(-2px,-2px) scale(1.02); box-shadow: 16px 16px 0 var(--ink); background: ${LIME}; animation: none; }
.hyperds .hd-submit:active:not(:disabled) { transform: rotate(0) translate(3px,3px); box-shadow: var(--shadow-sm); animation: none; }
.hyperds .hd-submit:disabled { opacity: .42; filter: grayscale(.5); cursor: default; box-shadow: var(--shadow); background: #cfcabc; }

.hyperds .hd-verdict { margin-top: 28px; text-align: center; opacity: 0; transform: translateY(40px) scale(.9); }
.hyperds .hd-verdict.show { opacity: 1; transform: translateY(0) scale(1); transition: transform .6s var(--spring-big), opacity .4s ease; }
.hyperds .hd-score-chip { display: inline-block; font-weight: 800; font-size: 2rem; text-transform: uppercase; background: ${YELLOW}; color: ${INK}; -webkit-text-stroke: 1px var(--ink); border: var(--border); border-radius: 16px; padding: 10px 24px; box-shadow: var(--shadow); transform: rotate(-2deg); }
.hyperds .hd-score-chip.perfect { background: ${LIME}; }
.hyperds .hd-verdict-msg { margin-top: 16px; font-weight: 600; font-size: 1.05rem; line-height: 1.45; max-width: 560px; margin-left: auto; margin-right: auto; }
.hyperds .hd-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; margin-top: 22px; }
.hyperds .hd-action-btn { font-weight: 800; font-size: 1rem; text-transform: uppercase; letter-spacing: .03em; color: var(--ink); background: #fff; border: var(--border); border-radius: 14px; padding: 12px 22px; box-shadow: var(--shadow-sm); cursor: pointer; transition: transform .12s var(--spring), box-shadow .12s var(--spring); }
.hyperds .hd-action-btn:hover { transform: translate(-2px,-2px); box-shadow: var(--shadow); }
.hyperds .hd-action-btn:active { transform: translate(2px,2px); box-shadow: 1px 1px 0 var(--ink); }
.hyperds .hd-action-btn.fix { background: ${PINK}; color: #fff; -webkit-text-stroke: .5px var(--ink); }
.hyperds .hd-close { margin-top: 26px; text-align: left; max-width: 640px; margin-left: auto; margin-right: auto; border: var(--border); border-radius: 22px; box-shadow: var(--shadow); background: #fff; padding: 8px; transform: rotate(.4deg); }

.hyperds .hd-flash { position: fixed; inset: 0; z-index: 60; pointer-events: none; background: #fff; opacity: 0; }
.hyperds .hd-flash.go { animation: hdFlash .5s ease; }
@keyframes hdFlash { 0% { opacity: 0; } 10% { opacity: .8; } 100% { opacity: 0; } }
.hyperds canvas.hd-confetti { position: fixed; inset: 0; z-index: 55; pointer-events: none; }

@media (prefers-reduced-motion: reduce) {
  .hyperds * { animation: none !important; }
  .hyperds .hd-verdict { transition: none !important; opacity: 1; transform: none; }
  .hyperds .hd-verdict:not(.show) { opacity: 0; }
  .hyperds .hd-submit:not(:disabled) { animation: none !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function HyperpopDragSort({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: DragSortSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();
  useScopedStyles();
  const { enabled: audioOn, toggle: toggleAudio, play, playWith } = useThemeWorldAudio('hyperpop');
  const headingFont = theme.fonts.heading;

  const hasItems = data.items.length > 0;

  // Correct = right zone, plus right position when sequence mode authors one
  // (mirrors DragSortSlide.isItemCorrect).
  const isItemCorrect = useCallback(
    (item: DragSortSlideData['items'][number], zoneId: string, position: number) =>
      item.correctZone === zoneId &&
      (data.mode !== 'sequence' || item.correctPosition === undefined || item.correctPosition === position),
    [data.mode],
  );

  // A saved payload's placements ({ itemId, zoneId, position? }) fully
  // reconstruct the submitted state (order included), so restore straight into
  // the result view.
  const savedPlacements = useMemo(() => {
    const raw = savedResponse?.placements;
    if (!Array.isArray(raw)) return null;
    return raw.filter(
      (p): p is { itemId: string; zoneId: string; position?: number } =>
        !!p &&
        typeof p === 'object' &&
        typeof (p as { itemId?: unknown }).itemId === 'string' &&
        typeof (p as { zoneId?: unknown }).zoneId === 'string',
    );
  }, [savedResponse]);

  // ── State machine (mirrors DragSortSlide's Student) ──────────────────────────
  // zoneId → ordered itemIds; items in no zone are still in the pool.
  const [zoneOrder, setZoneOrder] = useState<Record<string, string[]>>(() => {
    const next: Record<string, string[]> = {};
    for (const zone of data.zones) next[zone.id] = [];
    if (savedPlacements) {
      const sorted = [...savedPlacements].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      for (const p of sorted) {
        if (next[p.zoneId] && data.items.some((i) => i.id === p.itemId)) next[p.zoneId].push(p.itemId);
      }
    }
    return next;
  });
  const [submitted, setSubmitted] = useState(!!savedPlacements);
  const [feedback, setFeedback] = useState<{ correct: number; total: number } | null>(null);
  const completedRef = useRef(!!savedPlacements);

  // Tap-to-select touch fallback: the currently picked-up chip (tap a chip,
  // then tap a bin). Coexists with drag + keyboard.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Correction loop: round counter + the score to beat, for the round chip.
  const [round, setRound] = useState(1);
  const [prevScore, setPrevScore] = useState<number | null>(null);

  // A small drag-activation distance so a plain tap never starts a drag —
  // it falls through to the chip's click handler (tap-to-select) instead.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (submitted) return;
      const itemId = String(event.active.id);
      const zoneId = event.over ? String(event.over.id) : POOL_ZONE;
      setZoneOrder((prev) => {
        const next: Record<string, string[]> = {};
        for (const [zid, ids] of Object.entries(prev)) next[zid] = ids.filter((id) => id !== itemId);
        if (zoneId !== POOL_ZONE && next[zoneId]) next[zoneId] = [...next[zoneId], itemId];
        return next;
      });
      setSelectedId(null);
      if (zoneId !== POOL_ZONE) play('commit'); // chunky drop slam
    },
    [submitted, play],
  );

  // Tap-to-select: tap a chip to pick it up (tap again to put it down)…
  const toggleSelect = useCallback(
    (itemId: string) => {
      if (submitted) return;
      setSelectedId((prev) => (prev === itemId ? null : itemId));
      play('select');
    },
    [submitted, play],
  );

  // …then tap a bin (or the pool) to place it. Same placement logic as a drop.
  const placeSelected = useCallback(
    (zoneId: string) => {
      if (submitted || !selectedId) return;
      const itemId = selectedId;
      setZoneOrder((prev) => {
        const next: Record<string, string[]> = {};
        for (const [zid, ids] of Object.entries(prev)) next[zid] = ids.filter((id) => id !== itemId);
        if (zoneId !== POOL_ZONE && next[zoneId]) next[zoneId] = [...next[zoneId], itemId];
        return next;
      });
      setSelectedId(null);
      if (zoneId !== POOL_ZONE) play('commit');
    },
    [submitted, selectedId, play],
  );

  // Sequence mode: reorder an item within its zone.
  const moveItem = useCallback(
    (zoneId: string, index: number, delta: number) => {
      if (submitted) return;
      setZoneOrder((prev) => {
        const ids = [...(prev[zoneId] ?? [])];
        const target = index + delta;
        if (target < 0 || target >= ids.length) return prev;
        [ids[index], ids[target]] = [ids[target], ids[index]];
        return { ...prev, [zoneId]: ids };
      });
      play('select');
    },
    [submitted, play],
  );

  const itemsByZone = useMemo(() => {
    const byZone: Record<string, DragSortSlideData['items']> = {};
    const placed = new Set<string>();
    for (const zone of data.zones) {
      byZone[zone.id] = (zoneOrder[zone.id] ?? [])
        .map((id) => data.items.find((i) => i.id === id))
        .filter((i): i is DragSortSlideData['items'][number] => !!i);
      for (const item of byZone[zone.id]) placed.add(item.id);
    }
    const pool = data.items.filter((i) => !placed.has(i.id));
    return { byZone, pool };
  }, [data.items, data.zones, zoneOrder]);

  const allPlaced = itemsByZone.pool.length === 0;

  // Restore the result view for a saved submission once items/zones resolve.
  useEffect(() => {
    if (!savedPlacements || feedback) return;
    let correct = 0;
    for (const zone of data.zones) {
      itemsByZone.byZone[zone.id].forEach((item, position) => {
        if (isItemCorrect(item, zone.id, position)) correct += 1;
      });
    }
    setFeedback({ correct, total: data.items.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Confetti + flash ─────────────────────────────────────────────────────────
  const [flash, setFlash] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const partsRef = useRef<Array<Record<string, number | string>>>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  const confettiBurst = useCallback(
    (big: boolean) => {
      if (reduceMotion) return;
      const cv = canvasRef.current;
      if (!cv) return;
      cv.width = window.innerWidth;
      cv.height = window.innerHeight;
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      const n = big ? 170 : 80;
      for (let i = 0; i < n; i++) {
        partsRef.current.push({
          x: window.innerWidth / 2 + (Math.random() - 0.5) * 240,
          y: window.innerHeight * 0.45,
          vx: (Math.random() - 0.5) * 16,
          vy: Math.random() * -15 - 6,
          g: 0.45 + Math.random() * 0.25,
          s: 7 + Math.random() * 9,
          rot: Math.random() * 6.28,
          vr: (Math.random() - 0.5) * 0.4,
          c: CONFETTI[(Math.random() * CONFETTI.length) | 0],
          shape: Math.random() < 0.5 ? 'rect' : 'circ',
          life: 1,
        });
      }
      if (!rafRef.current) {
        const tick = () => {
          ctx.clearRect(0, 0, cv.width, cv.height);
          partsRef.current.forEach((p) => {
            p.vy = (p.vy as number) + (p.g as number);
            p.x = (p.x as number) + (p.vx as number);
            p.y = (p.y as number) + (p.vy as number);
            p.rot = (p.rot as number) + (p.vr as number);
            p.vx = (p.vx as number) * 0.99;
            if ((p.y as number) > window.innerHeight + 40) p.life = 0;
            ctx.save();
            ctx.translate(p.x as number, p.y as number);
            ctx.rotate(p.rot as number);
            ctx.fillStyle = p.c as string;
            ctx.strokeStyle = INK;
            ctx.lineWidth = 1.5;
            const s = p.s as number;
            if (p.shape === 'rect') { ctx.fillRect(-s / 2, -s / 2, s, s * 0.6); ctx.strokeRect(-s / 2, -s / 2, s, s * 0.6); }
            else { ctx.beginPath(); ctx.arc(0, 0, s * 0.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); }
            ctx.restore();
          });
          partsRef.current = partsRef.current.filter((p) => (p.life as number) > 0);
          if (partsRef.current.length) rafRef.current = requestAnimationFrame(tick);
          else { rafRef.current = 0; ctx.clearRect(0, 0, cv.width, cv.height); }
        };
        rafRef.current = requestAnimationFrame(tick);
      }
    },
    [reduceMotion],
  );

  const submit = useCallback(() => {
    if (!hasItems) return; // empty-items guard — never divide by zero
    const payload = data.items.map((item) => {
      const zoneId = data.zones.find((z) => (zoneOrder[z.id] ?? []).includes(item.id))?.id ?? POOL_ZONE;
      const entry: { itemId: string; zoneId: string; position?: number } = { itemId: item.id, zoneId };
      if (data.mode === 'sequence' && zoneId !== POOL_ZONE) {
        entry.position = (zoneOrder[zoneId] ?? []).indexOf(item.id);
      }
      return entry;
    });
    const correct = payload.filter((p) => {
      const item = data.items.find((i) => i.id === p.itemId);
      return !!item && isItemCorrect(item, p.zoneId, p.position ?? -1);
    }).length;
    const perfect = correct === data.items.length;
    setFeedback({ correct, total: data.items.length });
    setSubmitted(true);

    onActivityResponse?.({
      activityType: 'drag-sort',
      mode: 'quiz',
      responseData: { placements: payload },
      isCorrect: perfect,
      score: Math.round((correct / data.items.length) * 100),
    });
    // Terminal completion point — the single "lock in my sort" submit.
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }

    if (!reduceMotion) { setFlash(true); window.setTimeout(() => setFlash(false), 500); }
    confettiBurst(perfect);
    if (perfect) playWith(playHyperpopWin);
    else { playWith(playHyperpopLose); window.setTimeout(() => playWith(playHyperpopWin), 600); }
  }, [hasItems, data.items, data.zones, data.mode, zoneOrder, isItemCorrect, onActivityResponse, onComplete, reduceMotion, confettiBurst, playWith]);

  // Correction loop — return ONLY the misplaced items to the pool for another
  // round. Correctly placed chips stay where they are; the next "lock in" emits
  // the full composite payload again, overwriting the record.
  const fixMisses = useCallback(() => {
    if (!feedback) return;
    const wrongIds = new Set<string>();
    for (const zone of data.zones) {
      itemsByZone.byZone[zone.id].forEach((item, position) => {
        if (!isItemCorrect(item, zone.id, position)) wrongIds.add(item.id);
      });
    }
    setZoneOrder((prev) => {
      const next: Record<string, string[]> = {};
      for (const [zid, ids] of Object.entries(prev)) next[zid] = ids.filter((id) => !wrongIds.has(id));
      return next;
    });
    setPrevScore(feedback.correct);
    setRound((r) => r + 1);
    setFeedback(null);
    setSubmitted(false);
    setSelectedId(null);
    play('select');
  }, [feedback, data.zones, itemsByZone, isItemCorrect, play]);

  // Full reset (registry minimum-viable): everything back to the pool, clean board.
  const startOver = useCallback(() => {
    const next: Record<string, string[]> = {};
    for (const zone of data.zones) next[zone.id] = [];
    setZoneOrder(next);
    setSubmitted(false);
    setFeedback(null);
    setSelectedId(null);
    setRound(1);
    setPrevScore(null);
    play('select');
  }, [data.zones, play]);

  const perfect = feedback ? feedback.correct === feedback.total : false;

  return (
    <div className="hyperds h-full w-full overflow-y-auto" style={{ background: theme.colors.background, color: INK, fontFamily: theme.fonts.body }}>
      <ThemeWorldAtmosphere worldId="hyperpop" primary={BLUE} accent={PINK} background={theme.colors.background} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />
      <div className={`hd-flash${flash ? ' go' : ''}`} aria-hidden />
      <canvas ref={canvasRef} className="hd-confetti" aria-hidden />

      <div className="relative" style={{ zIndex: 5 }}>
        <div style={{ maxWidth: 880, margin: '0 auto', padding: '78px 22px 90px' }}>
          <div style={{ textAlign: 'center' }}>
            <span className="hd-kicker" style={{ fontFamily: headingFont }}>★ Sort It Out! ★</span>
          </div>
          <h1 className="hd-h1" style={{ fontFamily: headingFont }}>{data.title}</h1>
          {data.subtitle && <p style={{ fontWeight: 600, fontSize: '1.02rem', textAlign: 'center', opacity: 0.8, marginTop: 2 }}>{data.subtitle}</p>}
          {data.instruction && <div className="hd-instruction">{data.instruction}</div>}
          {round > 1 && !submitted && prevScore !== null && (
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <span className="hd-round-chip" style={{ fontFamily: headingFont }}>
                Round {round} · beat {prevScore}/{data.items.length}!
              </span>
            </div>
          )}

          {!hasItems ? (
            <div style={{ marginTop: 28 }}>
              <DropBin id={POOL_ZONE} label="Items" ink={INK} pool>
                <span className="hd-zone-empty">No items to sort yet — this activity has no content.</span>
              </DropBin>
            </div>
          ) : (
            <DndContext sensors={sensors} onDragStart={() => { if (!submitted) play('select'); }} onDragEnd={handleDragEnd}>
              {/* pool */}
              <div style={{ marginTop: 28 }}>
                <DropBin
                  id={POOL_ZONE}
                  label="Items"
                  ink={INK}
                  pool
                  targetable={!!selectedId}
                  onZoneClick={() => placeSelected(POOL_ZONE)}
                >
                  {itemsByZone.pool.map((item) => (
                    <Chip
                      key={item.id}
                      id={item.id}
                      text={item.text}
                      disabled={submitted}
                      selected={selectedId === item.id}
                      onTap={() => toggleSelect(item.id)}
                    />
                  ))}
                  {itemsByZone.pool.length === 0 && <span className="hd-zone-empty">All sorted! Hit the button.</span>}
                </DropBin>
                {selectedId && !submitted && <p className="hd-tap-hint">Picked up! Tap a bin to drop it.</p>}
              </div>

              {/* zones */}
              <div style={{ display: 'grid', gridTemplateColumns: data.zones.length > 1 ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr', gap: 16, marginTop: 16 }}>
                {data.zones.map((zone, zi) => (
                  <DropBin
                    key={zone.id}
                    id={zone.id}
                    label={zone.label}
                    ink={ZONE_INKS[zi % ZONE_INKS.length]}
                    font={headingFont}
                    targetable={!!selectedId}
                    onZoneClick={() => placeSelected(zone.id)}
                  >
                    {itemsByZone.byZone[zone.id].map((item, idx) => {
                      const state = submitted ? (isItemCorrect(item, zone.id, idx) ? 'correct' : 'wrong') : undefined;
                      return (
                        <Chip
                          key={item.id}
                          id={item.id}
                          text={item.text}
                          disabled={submitted}
                          state={state}
                          selected={selectedId === item.id}
                          onTap={() => toggleSelect(item.id)}
                          // Sequence mode: chunky up/down controls so the learner
                          // can ORDER items (captured as `position` on submit).
                          reorder={
                            data.mode === 'sequence' && !submitted
                              ? {
                                  canUp: idx > 0,
                                  canDown: idx < itemsByZone.byZone[zone.id].length - 1,
                                  onUp: () => moveItem(zone.id, idx, -1),
                                  onDown: () => moveItem(zone.id, idx, 1),
                                }
                              : undefined
                          }
                        />
                      );
                    })}
                    {itemsByZone.byZone[zone.id].length === 0 && <span className="hd-zone-empty">Drop here…</span>}
                  </DropBin>
                ))}
              </div>
            </DndContext>
          )}

          {hasItems && !submitted && (
            <button type="button" className="hd-submit" style={{ fontFamily: headingFont }} disabled={!allPlaced} onClick={submit}>
              🔒 Lock in my sort!
            </button>
          )}

          {feedback && (
            <div className={`hd-verdict${submitted ? ' show' : ''}`}>
              <div className={`hd-score-chip${perfect ? ' perfect' : ''}`} style={{ fontFamily: headingFont }}>
                {feedback.correct} / {feedback.total} correct
              </div>
              <p className="hd-verdict-msg">{perfect ? data.feedback.allCorrect : data.feedback.partial}</p>

              <div className="hd-actions">
                {!perfect && (
                  <button type="button" className="hd-action-btn fix" style={{ fontFamily: headingFont }} onClick={fixMisses}>
                    🔧 Fix the misses!
                  </button>
                )}
                <button type="button" className="hd-action-btn" style={{ fontFamily: headingFont }} onClick={startOver}>
                  ↻ Start over
                </button>
              </div>

              {/* Authored "So what?" close — shared renderer, hyperpop frame.
                  Same {correct, total} token values the standard skin passes. */}
              {data.conclusion && (
                <div className="hd-close">
                  <ExperienceClose
                    conclusion={data.conclusion}
                    theme={theme}
                    values={{ correct: feedback.correct, total: feedback.total }}
                    fallback="Look back at the items you misplaced above."
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function DropBin({
  id,
  label,
  ink,
  pool,
  font,
  targetable,
  onZoneClick,
  children,
}: {
  id: string;
  label: string;
  ink: string;
  pool?: boolean;
  font?: string;
  /** True while a chip is tap-selected — the bin is a live tap target. */
  targetable?: boolean;
  /** Tap-to-select placement: tapping the bin places the selected chip. */
  onZoneClick?: () => void;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`hd-zone${pool ? ' pool' : ''}${isOver ? ' over' : ''}${targetable ? ' targetable' : ''}`}
      onClick={onZoneClick}
    >
      <span className="hd-zone-label" style={{ background: ink, fontFamily: font }}>{label}</span>
      <div className="hd-chips">{children}</div>
    </div>
  );
}

function Chip({
  id,
  text,
  disabled,
  state,
  selected,
  onTap,
  reorder,
}: {
  id: string;
  text: string;
  disabled?: boolean;
  state?: 'correct' | 'wrong';
  /** Tap-to-select: this chip is currently picked up. */
  selected?: boolean;
  /** Tap-to-select toggle. Stops propagation so the bin's click doesn't fire. */
  onTap?: () => void;
  reorder?: { canUp: boolean; canDown: boolean; onUp: () => void; onDown: () => void };
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled });
  const style: React.CSSProperties = {
    // statement text stays in the body font (Hanken); Bricolage is reserved for
    // labels / headline / score chip.
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };
  const handleTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) onTap?.();
  };
  if (reorder) {
    // Wrap the draggable chip with sequence reorder controls. The arrow buttons
    // are NOT draggable (no drag listeners) so taps reorder rather than lift.
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <div
          ref={setNodeRef}
          {...listeners}
          {...attributes}
          className={`hd-chip${isDragging ? ' dragging' : ''}${selected ? ' selected' : ''}`}
          style={style}
          onClick={handleTap}
          aria-pressed={selected || undefined}
        >
          {text}
        </div>
        <span className="hd-reorder">
          <button
            type="button"
            className="hd-reorder-btn"
            disabled={!reorder.canUp}
            aria-label={`Move ${text} up`}
            onClick={reorder.onUp}
          >
            ▲
          </button>
          <button
            type="button"
            className="hd-reorder-btn"
            disabled={!reorder.canDown}
            aria-label={`Move ${text} down`}
            onClick={reorder.onDown}
          >
            ▼
          </button>
        </span>
      </div>
    );
  }
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`hd-chip${isDragging ? ' dragging' : ''}${state === 'correct' ? ' correct' : ''}${state === 'wrong' ? ' wrong' : ''}${selected ? ' selected' : ''}`}
      style={style}
      onClick={handleTap}
      aria-pressed={selected || undefined}
    >
      {text}
    </div>
  );
}
